import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import StandoutAthleteCard from '../components/StandoutAthleteCard';
import '../styles/StandoutsLanding.css';

const FilterDropdown = ({ iconSrc, value, options, onChange, isActive, searchable }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, []);

  const selected = options.find((o) => o.value === value);
  const visible = (searchable && query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options).filter((o) => !o.hidden);

  return (
    <div className="sland-filter-wrap" ref={ref}>
      <button
        type="button"
        className={`sland-filter-btn sland-filter-has-icon${isActive ? ' active' : ''}${open ? ' open' : ''}`}
        onClick={() => { setOpen((prev) => !prev); setQuery(''); }}
      >
        {iconSrc && <img src={iconSrc} alt="" className="sland-filter-icon" />}
        <span className="sland-filter-label">{selected ? selected.label : options[0]?.label}</span>
        <img src="/assets/images/filter-arrow-down.png" alt="" className="sland-filter-arrow" />
      </button>
      {open && (
        <div className="sland-filter-dropdown">
          {searchable && (
            <div style={{ padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <input
                autoFocus
                style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(216,161,52,0.3)', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {visible.map((opt) => (
            <div
              key={opt.value}
              className={`sland-filter-option${opt.value === value ? ' selected' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); setQuery(''); }}
              onTouchEnd={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); setQuery(''); }}
            >
              {opt.label}
            </div>
          ))}
          {visible.length === 0 && (
            <div style={{ padding: '10px 14px', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>No results</div>
          )}
        </div>
      )}
    </div>
  );
};

const StandoutPlaceholderCard = () => (
  <div className="sac-card sland-placeholder-card">
    <div className="sland-placeholder-inner">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(255,209,57,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
      <h3 className="sland-placeholder-heading">This Week's Standouts</h3>
      <p className="sland-placeholder-sub">Standouts will appear here once submissions are approved.</p>
    </div>
  </div>
);

const formatName = (n) => {
  if (!n) return '';
  return n.trim();
};

const getCurrentSeasonLabel = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  return month >= 1 && month <= 6 ? `Spring ${year}` : `Fall ${year}`;
};

const INITIAL_SHOW_DESKTOP = 24;
const INITIAL_SHOW_MOBILE = 20;
const LOAD_MORE_BATCH = 12;

const StandoutsLanding = () => {
  const navigate = useNavigate();
  const { getApprovedStandouts, getApprovedOrganizations } = useAuth();

  const [standouts, setStandouts] = useState([]);
  const [allApprovedOrgs, setAllApprovedOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [visibleCount, setVisibleCount] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [selectedWeek, setSelectedWeek] = useState('current');
  const [selectedSeason, setSelectedSeason] = useState('all');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
      setVisibleCount(null);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [standoutsResult, orgsResult] = await Promise.all([
        getApprovedStandouts(),
        getApprovedOrganizations(),
      ]);
      if (cancelled) return;
      if (standoutsResult.success) setStandouts(standoutsResult.data || []);
      if (orgsResult.success) setAllApprovedOrgs(orgsResult.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [getApprovedStandouts, getApprovedOrganizations]);

  // Determine which standouts to show based on selected week
  const weekStandouts = (() => {
    if (standouts.length === 0) return [];

    if (selectedWeek === 'current') {
      const now = new Date();
      const day = now.getDay(); // 0=Sun, 1=Mon
      // Find the most recent Monday 5pm
      const lastMonday = new Date(now);
      const daysBack = day === 0 ? 6 : day - 1;
      lastMonday.setDate(now.getDate() - daysBack);
      lastMonday.setHours(5, 0, 0, 0);
      // If we're Monday but before 5am, go back another 7 days
      if (now < lastMonday) lastMonday.setDate(lastMonday.getDate() - 7);
      return standouts.filter((s) => {
        const d = new Date(s.published_at || s.created_at);
        return d >= lastMonday;
      });
    }

    if (selectedWeek === 'last_week') {
      const now = new Date();
      const day = now.getDay();
      const startOfThisWeek = new Date(now);
      startOfThisWeek.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      startOfThisWeek.setHours(0, 0, 0, 0);
      const startOfLastWeek = new Date(startOfThisWeek);
      startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);
      return standouts.filter((s) => {
        const d = new Date(s.published_at || s.created_at);
        return d >= startOfLastWeek && d < startOfThisWeek;
      });
    }

    if (selectedWeek === 'this_month') {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      return standouts.filter((s) => new Date(s.published_at || s.created_at) >= cutoff);
    }

    if (selectedWeek === 'this') {
      const year = new Date().getFullYear();
      if (selectedSeason === 'all') return standouts.filter((s) => s.season?.endsWith(` ${year}`));
      return standouts.filter((s) => s.season === `${selectedSeason} ${year}`);
    }

    if (selectedWeek === 'this_year') {
      const year = new Date().getFullYear();
      return standouts.filter((s) => {
        const d = new Date(s.published_at || s.created_at);
        return d.getFullYear() === year;
      });
    }

    return standouts;
  })();

  // Build org/sport/location filter options from current standouts
  const orgsMap = new Map();
  weekStandouts.forEach((s) => {
    if (s.org_slug && !orgsMap.has(s.org_slug)) {
      orgsMap.set(s.org_slug, { id: s.org_slug, name: s.org_name, location: s.org_location });
    }
  });
  const organizations = Array.from(orgsMap.values());

  const allSports = [...new Set(weekStandouts.map((s) => s.sport).filter(Boolean))];
  const normalizeLocation = (l) => {
    const parts = l.split(',');
    if (parts.length >= 2) return `${parts[0].trim()}, ${parts[1].trim().toUpperCase()}`;
    return l.trim();
  };

  const allLocations = [
    ...new Map(
      weekStandouts
        .map((s) => s.org_location)
        .filter(Boolean)
        .map((l) => [l.toUpperCase(), normalizeLocation(l)])
    ).values(),
  ];

  const filtered = weekStandouts.filter((s) => {
    return (
      (selectedOrg === 'all' || s.org_slug === selectedOrg) &&
      (selectedSport === 'all' || s.sport === selectedSport) &&
      (selectedLocation === 'all' || s.org_location?.toUpperCase() === selectedLocation?.toUpperCase())
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const initialShow = isMobile ? INITIAL_SHOW_MOBILE : INITIAL_SHOW_DESKTOP;
  const currentLimit = visibleCount ?? initialShow;
  const displayed = sorted.slice(0, currentLimit);
  const hasMore = sorted.length > currentLimit;

  return (
    <div className="sland-page">

      {/* ── Topbar ── */}
      <div className="sland-topbar">
        <img src="/assets/images/logo.png" alt="LegacyStat" className="sland-logo" />
        <div className="sland-tabs">
          <div className="sland-tab-group">
            <button className="sland-tab-btn" onClick={() => navigate('/')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              Spotlight
            </button>
            <button className="sland-tab-btn sland-tab-active">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
              Standouts
            </button>
          </div>
        </div>
      </div>

      <div className="sland-inner">

        {/* ── Hero Title ── */}
        <div className="sland-hero">
          <h1 className="sland-title">STANDOUTS</h1>
          <div className="sland-coaches-row">
            <span className="sland-dash" />
            <span className="sland-coaches-text">COACH SELECTED</span>
            <span className="sland-dash" />
          </div>
          <p className="sland-tagline">Weekly recognition for standout performances.</p>
          <p className="sland-org-cta">
            <button className="sland-org-link" onClick={() => navigate('/standouts/submit')}>
              → Add your Standouts
            </button>
          </p>
        </div>

        {/* ── Filters ── */}
        <div className="sland-filters">
          <FilterDropdown
            iconSrc="/assets/images/filter-org.png"
            value={selectedOrg}
            options={[
              { value: 'all', label: 'Organizations', hidden: true },
              ...allApprovedOrgs.map((o) => ({ value: o.slug || o.id, label: o.name })),
            ]}
            onChange={(v) => { if (v !== 'all') navigate(`/standouts/org/${v}`); }}
            isActive={selectedOrg !== 'all'}
            searchable
          />

          <FilterDropdown
            iconSrc="/assets/images/filter-sports.png"
            value={selectedSport}
            options={[
              { value: 'all', label: 'Sports' },
              ...allSports.map((s) => ({ value: s, label: s })),
            ]}
            onChange={(v) => { setSelectedSport(v); setVisibleCount(null); }}
            isActive={selectedSport !== 'all'}
          />

          <FilterDropdown
            iconSrc="/assets/images/filter-location.png"
            value={selectedLocation}
            options={[
              { value: 'all', label: 'Locations' },
              ...allLocations.map((l) => ({ value: l, label: l })),
            ]}
            onChange={(v) => { setSelectedLocation(v); setVisibleCount(null); }}
            isActive={selectedLocation !== 'all'}
          />

          <FilterDropdown
            iconSrc="/assets/images/filter-recent.png"
            value={selectedWeek}
            options={[
              { value: 'current', label: 'This Week' },
              { value: 'last_week', label: 'Last Week' },
              { value: 'this_month', label: 'This Month' },
              { value: 'this', label: 'This Season' },
              { value: 'this_year', label: 'This Year' },
            ]}
            onChange={(v) => { setSelectedWeek(v); setVisibleCount(null); if (v !== 'this') setSelectedSeason('all'); }}
            isActive={selectedWeek !== 'current'}
          />

          {selectedWeek === 'this' && (
            <FilterDropdown
              iconSrc="/assets/images/filter-recent.png"
              value={selectedSeason}
              options={[
                { value: 'all', label: 'All Seasons' },
                { value: 'Spring', label: 'Spring' },
                { value: 'Summer', label: 'Summer' },
                { value: 'Fall', label: 'Fall' },
                { value: 'Winter', label: 'Winter' },
              ]}
              onChange={(v) => { setSelectedSeason(v); setVisibleCount(null); }}
              isActive={selectedSeason !== 'all'}
            />
          )}
        </div>

        {/* ── Grid ── */}
        {(() => {
          const filtersActive = selectedOrg !== 'all' || selectedSport !== 'all' || selectedLocation !== 'all';
          const placeholderCount = (!hasMore && !(filtersActive && displayed.length === 0))
            ? Math.max(0, initialShow - displayed.length)
            : 0;

          if (loading) return (
            <div className="sland-grid">
              {Array.from({ length: initialShow }).map((_, i) => (
                <div key={i} className="sland-skel-card" />
              ))}
            </div>
          );
          if (filtersActive && displayed.length === 0) return <p className="sland-empty">No standouts match your filters.</p>;

          return (
            <div className="sland-grid">
              {displayed.map((s) => (
                <StandoutAthleteCard
                  key={s.id}
                  image={s.photo_url}
                  name={formatName(s.athlete_name)}
                  sport={s.org_name}
                  sportType={s.sport}
                  award={s.award}
                  week={s.week_label}
                  level={s.level}
                  onBadgeClick={() => navigate(`/standouts/athlete/${s.id}`)}
                  onOrgClick={() => navigate(`/standouts/org/${s.org_slug}`)}
                />
              ))}
              {Array.from({ length: placeholderCount }).map((_, i) => (
                <StandoutPlaceholderCard key={`ph-${i}`} />
              ))}
            </div>
          );
        })()}

        {/* ── Load More ── */}
        {hasMore && (
          <div className="sland-load-more-row">
            <button className="sland-load-more-btn" onClick={() => setVisibleCount((prev) => (prev ?? initialShow) + LOAD_MORE_BATCH)}>
              <img src="/assets/images/load-more.png" alt="" className="sland-load-more-icon" />
              Load More Standouts
            </button>
          </div>
        )}

        {/* ── Org Banner ── */}
        <div className="sland-org-banner">
          <div className="sland-banner-left">
            <div className="sland-banner-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="9" cy="7" r="4" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#D8A134" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <p className="sland-banner-title">Get your organization on LegacyStat</p>
              <p className="sland-banner-sub">Nominate players. Publish standouts weekly. Build your organization's presence.</p>
            </div>
          </div>
          <button className="sland-banner-btn" onClick={() => navigate('/standouts/submit')}>
            Add Your Organizations Standouts
          </button>
        </div>

      </div>

      {/* ── Footer ── */}
      <footer className="sland-footer">
        <div className="sland-footer-left">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="sland-footer-logo" />
        </div>
        <p className="sland-footer-copy">© 2026 LegacyStat. All rights reserved.</p>
        <div className="sland-footer-links">
          <a href="/privacy" className="sland-footer-link">Privacy Policy</a>
          <span className="sland-footer-sep">|</span>
          <a href="/terms" className="sland-footer-link">Terms of Service</a>
        </div>
      </footer>

    </div>
  );
};

export default StandoutsLanding;
